import { useCallback, useEffect, useState } from "react";
import { Sparkles, Loader2, FileDown, FileType, Trash2, Copy, Save, Mail, Send, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, fmtDate, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Saved = ({ letters, onOpen, onDelete, onSent, onFollowup }) => (
  <div className="rounded-3xl border bg-white p-6">
    <h3 className="font-medium">Lettere salvate</h3>
    {letters.length === 0 && <p className="mt-2 text-sm text-slate-500">Nessuna lettera salvata.</p>}
    {letters.map((l) => (
      <div key={l.letter_id} className="mt-3 rounded-2xl border p-4" data-testid={`letter-item-${l.letter_id}`}>
        <button onClick={() => onOpen(l)} className="text-left w-full"><p className="font-medium text-sm truncate">{l.title}</p><p className="text-xs text-slate-500">{l.is_pec ? "PEC · " : ""}{fmtDate(l.created_at)}{l.sent_at ? ` · inviata, sollecito il ${fmtDate(l.followup_date)}` : ""}</p></button>
        <div className="mt-3 flex flex-wrap gap-2">
          {!l.sent_at && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onSent(l)} data-testid={`letter-sent-${l.letter_id}`}><Send className="h-4 w-4 mr-1" /> Inviata</Button>}
          {l.sent_at && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onFollowup(l)} data-testid={`letter-followup-${l.letter_id}`}><BellRing className="h-4 w-4 mr-1" /> Sollecito</Button>}
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadFile(`/letters/${l.letter_id}/export?format=pdf`, `${l.title}.pdf`)} data-testid={`letter-pdf-${l.letter_id}`}><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadFile(`/letters/${l.letter_id}/export?format=docx`, `${l.title}.docx`)} data-testid={`letter-docx-${l.letter_id}`}><FileType className="h-4 w-4 mr-1" /> Word</Button>
          <Button size="sm" variant="ghost" className="rounded-full text-red-600 ml-auto" onClick={() => onDelete(l)} data-testid={`letter-delete-${l.letter_id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    ))}
  </div>
);

export default function Letters() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [fields, setFields] = useState({});
  const [sender, setSender] = useState({ name: user?.name || "", address: "" });
  const [extra, setExtra] = useState("");
  const [draft, setDraft] = useState({ id: null, title: "", body: "", is_pec: true, template_id: null });
  const [busy, setBusy] = useState(false);
  const [letters, setLetters] = useState([]);
  const [sending, setSending] = useState(null);

  const load = useCallback(() => {
    api.get("/letters").then(({ data }) => setLetters(data));
  }, []);
  useEffect(() => {
    api.get("/letters/templates").then(({ data }) => {
      setTemplates(data);
      setTpl(data[0]);
    });
    load();
  }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/letters/draft", { template_id: tpl.id, fields, sender_name: sender.name, sender_address: sender.address, extra });
      setDraft({ id: null, title: data.title, body: data.body, is_pec: draft.is_pec, template_id: tpl.id });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    const body = { title: draft.title || tpl.title, body: draft.body, is_pec: draft.is_pec, template_id: draft.template_id || tpl?.id || "pec-generica" };
    try {
      const { data } = draft.id ? await api.put(`/letters/${draft.id}`, body) : await api.post("/letters", body);
      setDraft((d) => ({ ...d, id: data.letter_id }));
      toast.success("Lettera salvata: ora puoi esportarla");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (l) => {
    if (!window.confirm(`Eliminare "${l.title}"?`)) return;
    await api.delete(`/letters/${l.letter_id}`);
    if (draft.id === l.letter_id) setDraft({ id: null, title: "", body: "", is_pec: true });
    load();
  };
  const markSent = async (l) => {
    if (sending) return;
    setSending(l.letter_id);
    try {
      const { data } = await api.post(`/letters/${l.letter_id}/sent`, { days: 30 });
      toast.success(`Ti ricorderemo di sollecitare il ${fmtDate(data.followup_date)} se non rispondono`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(null);
    }
  };
  const followup = async (l) => {
    toast.info("Scrivo il sollecito…");
    try {
      const { data } = await api.post(`/letters/${l.letter_id}/followup`);
      setDraft({ id: data.letter_id, title: data.title, body: data.body, is_pec: data.is_pec, template_id: data.template_id });
      load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const mailto = `mailto:?subject=${encodeURIComponent(draft.title)}&body=${encodeURIComponent(draft.body)}`;

  return (
    <div data-testid="letters-page">
      <PageHeader eyebrow="Generatore" title="Lettere e PEC" desc="Scegli un modello, inserisci i dati e lascia che Burocrazy scriva per te. Esporta in PDF o Word." />
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {templates.map((t) => (
          <button key={t.id} onClick={() => { setTpl(t); setFields({}); }} data-testid={`template-${t.id}`}
            className={`shrink-0 rounded-2xl border px-4 py-3 text-left transition-colors ${tpl?.id === t.id ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white hover:bg-slate-50"}`}>
            <p className="text-sm font-medium">{t.title}</p><p className={`text-xs ${tpl?.id === t.id ? "text-blue-100" : "text-slate-500"}`}>{t.desc}</p>
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="rounded-3xl border bg-white p-6 space-y-4 h-fit">
          <h3 className="font-medium">Dati della lettera</h3>
          <div><Label>Mittente</Label><Input value={sender.name} onChange={(e) => setSender({ ...sender, name: e.target.value })} data-testid="letter-sender-name" /></div>
          <div><Label>Indirizzo mittente</Label><Input value={sender.address} onChange={(e) => setSender({ ...sender, address: e.target.value })} data-testid="letter-sender-address" /></div>
          {tpl?.fields.map((f, i) => (
            <div key={f}><Label>{f}</Label><Input value={fields[f] || ""} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} data-testid={`letter-field-${i}`} /></div>
          ))}
          <div><Label>Altro da aggiungere</Label><Textarea rows={3} value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="letter-extra-input" /></div>
          <Button onClick={generate} disabled={busy || !tpl} className="w-full rounded-full" data-testid="letter-generate-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} {busy ? "Sto scrivendo…" : "Genera con AI"}
          </Button>
        </div>
        <div className="rounded-3xl border bg-white p-6 space-y-4">
          <h3 className="font-medium">Anteprima modificabile</h3>
          <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Titolo / oggetto" data-testid="letter-title-input" />
          <Textarea rows={18} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="font-serif text-[15px] leading-relaxed" placeholder="Il testo generato apparirà qui. Puoi anche scrivere da zero." data-testid="letter-body-input" />
          <label className="flex items-center justify-between text-sm">Da inviare via PEC<Switch checked={draft.is_pec} onCheckedChange={(v) => setDraft({ ...draft, is_pec: v })} data-testid="letter-pec-switch" /></label>
          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={!draft.body} className="rounded-full" data-testid="letter-save-btn"><Save className="h-4 w-4 mr-2" /> Salva</Button>
            <Button variant="outline" className="rounded-full" disabled={!draft.body} onClick={() => { navigator.clipboard.writeText(draft.body); toast.success("Testo copiato"); }} data-testid="letter-copy-btn"><Copy className="h-4 w-4 mr-2" /> Copia</Button>
            <Button asChild variant="outline" className="rounded-full" disabled={!draft.body}><a href={mailto} data-testid="letter-mail-btn"><Mail className="h-4 w-4 mr-2" /> Apri nella posta</a></Button>
          </div>
          {draft.is_pec && <p className="text-xs text-slate-500">Per il valore legale invia il testo (o il PDF allegato) dalla tua casella PEC personale.</p>}
        </div>
        <Saved letters={letters} onOpen={(l) => setDraft({ id: l.letter_id, title: l.title, body: l.body, is_pec: l.is_pec, template_id: l.template_id })} onDelete={remove} onSent={markSent} onFollowup={followup} />
      </div>
    </div>
  );
}
